import { useState } from "react"
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material"
import PlayIcon from "@mui/icons-material/PlayCircleOutlineOutlined"
import StopIcon from "@mui/icons-material/StopCircleOutlined"

import { useTimer } from "../context/TimerContext"

/**
 * Start or stop the timer for one task.
 *
 * Shows STOP when the running timer is this task's, so the same control both
 * starts and ends a stint — a separate stop button elsewhere is how people end
 * up with two timers running, or with none when they thought they had one.
 */
export default function StartTimerButton({ taskId, projectId, clientId, description, icon = false, size = "small" }) {
  const { running, busy, mayTrack, start, stop } = useTimer()
  const [pending, setPending] = useState(false)

  if (!mayTrack) return null

  const isThisTask = taskId != null && running?.task_id != null && Number(running.task_id) === Number(taskId)
  const working = busy || pending

  const onClick = async (event) => {
    // These sit inside clickable rows and cards.
    event.stopPropagation()
    event.preventDefault()
    setPending(true)
    try {
      if (isThisTask) await stop()
      else await start({ task_id: taskId, project_id: projectId, client_id: clientId, description })
    } catch {
      // The bar reports it.
    } finally {
      setPending(false)
    }
  }

  const label = isThisTask ? "Stop the timer on this task" : "Start a timer on this task"
  const Glyph = isThisTask ? StopIcon : PlayIcon

  if (icon) {
    return (
      <Tooltip title={label}>
        <span>
          <IconButton size={size} onClick={onClick} disabled={working} aria-label={label} color={isThisTask ? "warning" : "default"}>
            {working ? <CircularProgress size={16} /> : <Glyph fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  return (
    <Button
      size={size}
      variant={isThisTask ? "contained" : "outlined"}
      color={isThisTask ? "warning" : "primary"}
      startIcon={working ? <CircularProgress size={14} color="inherit" /> : <Glyph />}
      onClick={onClick}
      disabled={working}
    >
      {isThisTask ? "Stop" : "Start timer"}
    </Button>
  )
}
